import {
    faAngleDoubleLeft,
    faAngleDoubleRight,
    faCheckCircle,
    faExternalLink,
    faEye,
    faSpinner,
    faWallet,
    faWarning,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Copyable } from '~/components/ui/Copyable'
import { formatWalletAddress } from '~/features/bank/utils'
import { useDepositList } from '../api/useDepositList'
import { formatTxURL } from '../utils/solscan'

const usdcIcon = new URL('~/features/bank/assets/usdc.png', import.meta.url).toString()
const solIcon = new URL('~/features/bank/assets/solana.png', import.meta.url).toString()

/** Mirrors `Deposit.Status` on the server. */
const STATUS = {
    WAITING: 0,
    CONFIRMING: 1,
    CONFIRMED: 2,
}

// "Checking" reads as a review the payment might fail. "Confirming" says the same wait is a step
// that completes on its own.
//
// `rail` is the coloured edge down the left of the row. It carries the state from the far side of
// the list, which the old design left entirely to the colour of the label text.
const statuses = {
    [STATUS.WAITING]: {
        name: 'Waiting for payment',
        icon: faEye,
        color: 'text-white',
        rail: 'border-white/40',
    },
    [STATUS.CONFIRMING]: {
        name: 'Confirming',
        icon: faSpinner,
        color: 'text-yellow-500',
        rail: 'border-yellow-500',
    },
    [STATUS.CONFIRMED]: {
        name: 'Confirmed',
        icon: faCheckCircle,
        color: 'text-green-500',
        rail: 'border-green-500/60',
    },
}

// The list is filtered to `status > 0` server-side, so only CONFIRMING and CONFIRMED can arrive.
// That filter is the only thing standing between this map and a crash: the moment anyone widens it
// to surface failed deposits (`ERROR` is -1), an unguarded `statuses[-1].color` throws and takes
// the whole list down with it. A fallback is cheaper than that coupling.
const unknownStatus = {
    name: 'Unknown',
    icon: faWarning,
    color: 'text-white/60',
    rail: 'border-white/20',
}

const statusOf = (status: number) => statuses[status] ?? unknownStatus

/**
 * How full to draw the bar for a deposit that has been confirming for `elapsedSeconds`.
 *
 * Deliberately not linear, and deliberately incapable of reaching the end.
 *
 * Nothing on the wire says how far along a confirmation is -- the server asks the chain a yes/no
 * question and either completes the deposit or waits to ask again -- so any bar drawn here is an
 * estimate of elapsed time, not a measurement of progress. A linear bar has to name a finish time,
 * and the moment it arrives full with nothing confirmed it has visibly lied.
 *
 * An exponential approach cannot: it is fast across the window where confirmations actually land,
 * then flattens into a crawl, and the ceiling holds it short of the end however long the wait runs.
 * The bar completes only because the state changed, which is the honest contract.
 *
 *   10s -> 34%    30s -> 68%    60s -> 87%    5m -> 94%    forever -> 94%
 */
const TIME_CONSTANT_SECONDS = 22
const PROGRESS_CEILING = 0.94
const confirmingProgress = (elapsedSeconds: number) =>
    (1 - Math.exp(-Math.max(elapsedSeconds, 0) / TIME_CONSTANT_SECONDS)) * PROGRESS_CEILING

const formatElapsed = (elapsedSeconds: number) => {
    const seconds = Math.max(0, Math.floor(elapsedSeconds))
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`
    return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`
}

/**
 * A one-second tick, running only while something is waiting on it.
 *
 * One clock for the whole list rather than one per row, and cleared on unmount -- closing the modal
 * has to actually stop it, or every open-and-close leaves another timer running behind the page for
 * as long as the tab lives.
 */
const useElapsedClock = (active: boolean) => {
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        if (!active) return undefined
        setNow(Date.now())
        const interval = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(interval)
    }, [active])
    return now
}

export const DepositList = () => {
    const [currentPage, setCurrentPage] = useState(1)
    const { data, isLoading } = useDepositList({ page: currentPage })
    const hasConfirming = data?.data?.some((deposit) => deposit.status === STATUS.CONFIRMING) ?? false
    const now = useElapsedClock(hasConfirming)

    if (isLoading) {
        return (
            <div className='flex flex-col gap-2'>
                {[0, 1, 2].map((row) => (
                    <div key={row} className='h-12 animate-pulse border-l-2 border-white/10 bg-white/5' />
                ))}
            </div>
        )
    }

    // Nothing rendered here at all before, so a first-time depositor got a blank space below the
    // form with no indication that this is where their deposit is going to show up.
    if (!data?.count) {
        return (
            <p className='border-l-2 border-white/10 bg-white/[0.03] p-3 text-2xs uppercase tracking-wider text-white/35'>
                Your deposits will appear here
            </p>
        )
    }

    return (
        <div className='flex flex-col gap-2'>
            <div className='self-end'>
                <Page data={data} setCurrentPage={setCurrentPage} />
            </div>
            <div className='flex flex-col gap-2'>
                {data.data.map((deposit) => (
                    <Row key={deposit.uuid} deposit={deposit} now={now} />
                ))}
            </div>
            <div className='self-end'>
                <Page data={data} setCurrentPage={setCurrentPage} />
            </div>
        </div>
    )
}

const Page = ({ data, setCurrentPage }) => {
    return (
        <div className='flex flex-row gap-2 items-center text-xs select-none'>
            <button
                disabled={!data?.prev_page}
                className='border rounded p-1 border-white/10 enabled:hover:border-white/50 disabled:opacity-30'
                onClick={() => setCurrentPage((prev) => data?.prev_page || prev)}
            >
                <small>
                    <FontAwesomeIcon icon={faAngleDoubleLeft} />
                </small>
            </button>
            <div>{data?.page}</div>
            <div>/</div>
            <div>{data?.pages}</div>
            <button
                disabled={!data?.next_page}
                className='border rounded p-1 border-white/10 enabled:hover:border-white/50 disabled:opacity-30'
                onClick={() => setCurrentPage((prev) => data?.next_page || prev)}
            >
                <small>
                    <FontAwesomeIcon icon={faAngleDoubleRight} />
                </small>
            </button>
        </div>
    )
}

/**
 * One deposit.
 *
 * A confirming deposit is the only row anyone is actually watching, so it is the only one drawn as
 * a live card -- tinted, with a pulse, a bar and a running clock. Everything settled collapses back
 * into a quiet log line, because a list where every row shouts is a list where none of them do.
 */
const Row = ({ deposit, now }: { deposit: Deposit; now: number }) => {
    const [showDetails, setShowDetails] = useState(false)
    const status = statusOf(deposit.status)
    const confirming = deposit.status === STATUS.CONFIRMING
    const elapsedSeconds = (now - new Date(deposit.created_at).getTime()) / 1000

    return (
        <div
            className={twMerge(
                'flex flex-col border-l-2 bg-white/[0.03] text-xs font-bold transition-colors',
                status.rail,
                confirming && 'bg-yellow-500/[0.07]'
            )}
        >
            <button
                type='button'
                onClick={() => setShowDetails((prev) => !prev)}
                className='flex flex-col gap-0.5 p-2 text-left transition-colors hover:bg-white/[0.05]'
            >
                <div className='flex flex-row items-center gap-2'>
                    <div
                        className={twMerge(
                            'flex flex-1 flex-row items-center gap-2 uppercase tracking-wider',
                            status.color
                        )}
                    >
                        {confirming ? <PulseDot /> : <FontAwesomeIcon icon={status.icon} className='w-3 shrink-0' />}
                        <span>{status.name}</span>
                    </div>
                    <div className='flex shrink-0 flex-row items-center gap-1 tabular-nums'>
                        <span>{deposit.amount}</span>
                        <img src={deposit.type === 1 ? usdcIcon : solIcon} className='h-4 w-4' alt='' />
                    </div>
                </div>
                <small className='font-normal text-white/30'>{new Date(deposit.created_at).toLocaleString()}</small>
            </button>
            {confirming && <ConfirmingProgress elapsedSeconds={elapsedSeconds} />}
            {showDetails && <Details deposit={deposit} />}
        </div>
    )
}

const PulseDot = () => (
    <span className='relative flex h-2 w-2 shrink-0'>
        <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-500 opacity-75' />
        <span className='relative inline-flex h-2 w-2 rounded-full bg-yellow-500' />
    </span>
)

/**
 * The bar, its running clock, and the sentence that does most of the actual reassuring.
 *
 * `aria-valuenow` is left off deliberately. Omitting it is how ARIA spells "indeterminate", which is
 * the truth here -- the width is an estimate of elapsed time, so publishing it as a percentage would
 * state a precision that does not exist. `aria-valuetext` carries the part that is real: how long
 * this has been going.
 */
const ConfirmingProgress = ({ elapsedSeconds }: { elapsedSeconds: number }) => {
    const progress = confirmingProgress(elapsedSeconds)
    return (
        <div className='flex flex-col gap-1.5 px-2 pb-2'>
            <div
                role='progressbar'
                aria-valuetext={`Confirming for ${formatElapsed(elapsedSeconds)}`}
                className='relative h-1 w-full overflow-hidden bg-white/10'
            >
                <div
                    className='h-full bg-gradient-to-r from-yellow-600 to-yellow-400 transition-[width] duration-1000 ease-linear'
                    style={{ width: `${(progress * 100).toFixed(1)}%` }}
                />
                {/* A highlight travelling over the top, so the bar still reads as working through the
                    long flat stretch where its width barely changes. */}
                <div
                    className='pointer-events-none absolute inset-y-0 left-0 w-1/4 animate-progress-sweep bg-gradient-to-r from-transparent via-white/50 to-transparent'
                />
            </div>
            <div className='flex flex-row items-baseline justify-between gap-2 font-normal'>
                <span className='text-2xs leading-relaxed text-white/40'>
                    Usually under a minute. We keep checking either way.
                </span>
                <span className='shrink-0 text-2xs tabular-nums text-yellow-500/80'>
                    {formatElapsed(elapsedSeconds)}
                </span>
            </div>
        </div>
    )
}

const Details = ({ deposit }: { deposit: Deposit }) => (
    <div className='grid grid-cols-[max-content,1fr] gap-2 border-t border-white/10 p-2 font-normal'>
        <div className='text-white/50'>id:</div>
        <Copyable value={deposit.uuid}>
            <span className='break-all'>{deposit.uuid}</span>
        </Copyable>
        <div className='text-white/50'>
            <FontAwesomeIcon icon={faWallet} />
        </div>
        <Copyable value={deposit.wallet}>
            <div title={deposit.wallet}>{formatWalletAddress(deposit.wallet)}</div>
        </Copyable>
        {deposit.tx_signature && (
            <>
                <div className='text-white/50'>tx:</div>
                <div className='flex flex-row items-center gap-1'>
                    <a
                        href={formatTxURL(deposit.tx_signature, deposit.cluster)}
                        target='_blank'
                        title={deposit.tx_signature}
                        className='flex flex-row items-center gap-1 text-green-500 underline-offset-2 hover:underline'
                    >
                        <span>{formatWalletAddress(deposit.tx_signature)}</span>
                        <FontAwesomeIcon icon={faExternalLink} />
                    </a>
                    <Copyable value={deposit.tx_signature} />
                </div>
            </>
        )}
    </div>
)
